// Eloquent Clicks filter sheet (snippets/ec-filter-sheet.liquid).
// Native <dialog> (focus stays inside, Escape closes). Ticking updates the result count and the
// per-value counts by fetching the section in the background; "Show n results" applies the
// filters through the theme's AJAX update (EVENTS.ajaxCollectionRender).
import { EVENTS } from 'util.events'

const FILTER_PREFIX = 'filter.'

class EcFilterSheet extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog')
    this.form = this.querySelector('[data-ec-sheet-form]')
    this.applyButton = this.querySelector('[data-ec-sheet-apply]')
    this.sectionId = this.dataset.sectionId
    this.abortController = new AbortController()
    const options = { signal: this.abortController.signal }

    this.querySelector('[data-ec-sheet-close]').addEventListener('click', () => this.close(), options)
    this.querySelector('[data-ec-sheet-clear]').addEventListener('click', () => this.clear(), options)
    this.applyButton.addEventListener('click', () => this.apply(), options)
    this.form.addEventListener('change', () => this.scheduleCount(), options)
    this.form.addEventListener('submit', (evt) => { evt.preventDefault(); this.apply() }, options)

    // Click on the dimmed backdrop (outside the sheet box) closes it
    this.dialog.addEventListener('click', (evt) => {
      if (evt.target === this.dialog) this.close()
    }, options)
    // Escape: animate out instead of the instant native close
    this.dialog.addEventListener('cancel', (evt) => { evt.preventDefault(); this.close() }, options)
    this.dialog.addEventListener('close', () => this.afterClose(), options)

    this.initDrag(options)
  }

  disconnectedCallback() {
    this.abortController.abort()
    this.countController?.abort()
    document.documentElement.style.removeProperty('overflow')
  }

  open(trigger) {
    if (this.dialog.open) return
    this.trigger = trigger || null
    this.dialog.showModal()
    document.documentElement.style.overflow = 'hidden'
    requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-visible')))
  }

  close() {
    if (!this.dialog.open) return
    this.dialog.classList.remove('is-visible', 'is-dragging')
    this.dialog.style.removeProperty('transform')
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.setTimeout(() => this.dialog.close(), reduceMotion ? 250 : 350)
  }

  afterClose() {
    document.documentElement.style.removeProperty('overflow')
    if (this.trigger && this.trigger.isConnected) this.trigger.focus()
  }

  // URL for the current form state: keeps non-filter parameters, drops page
  buildUrl() {
    const url = new URL(window.location.href)
    ;[...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith(FILTER_PREFIX) || key === 'sort_by' || key === 'page') url.searchParams.delete(key)
    })
    new FormData(this.form).forEach((value, key) => {
      if (value === '') return
      // Leave the default sort out of the address
      if (key === 'sort_by' && value === this.form.dataset.defaultSort) return
      url.searchParams.append(key, value)
    })
    if (!this.form.querySelector('input[name="sort_by"]:checked')) {
      const current = new URL(window.location.href).searchParams.get('sort_by')
      if (current) url.searchParams.set('sort_by', current)
    }
    return url
  }

  clear() {
    this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false })
    this.form.querySelectorAll('input[type="number"]').forEach((input) => { input.value = '' })
    this.scheduleCount()
  }

  scheduleCount() {
    window.clearTimeout(this.countTimer)
    this.countTimer = window.setTimeout(() => this.updateCount(), 250)
  }

  async updateCount() {
    this.countController?.abort()
    this.countController = new AbortController()
    const url = this.buildUrl()
    url.searchParams.set('section_id', this.sectionId)
    this.applyButton.setAttribute('aria-busy', 'true')
    try {
      const response = await fetch(url, { signal: this.countController.signal })
      const html = new DOMParser().parseFromString(await response.text(), 'text/html')
      const countEl = html.querySelector('[data-ec-product-count]')
      if (countEl) this.setCount(Number(countEl.dataset.ecProductCount))

      // Per-value counts for the new selection
      const fresh = new Map()
      html.querySelectorAll('[data-ec-sheet-form] input[type="checkbox"]').forEach((input) => {
        const count = input.closest('label')?.querySelector('[data-ec-count]')?.dataset.ecCount
        fresh.set(`${input.name}|${input.value}`, count)
      })
      this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        const label = input.closest('label')
        const countEl = label.querySelector('[data-ec-count]')
        const count = fresh.has(`${input.name}|${input.value}`) ? fresh.get(`${input.name}|${input.value}`) : '0'
        if (countEl) countEl.textContent = count ?? '0'
        label.classList.toggle('is-empty', !input.checked && (count === undefined || count === '0'))
      })
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error)
    } finally {
      this.applyButton.removeAttribute('aria-busy')
    }
  }

  setCount(count) {
    const button = this.applyButton
    button.textContent = count === 1 ? button.dataset.labelOne : button.dataset.labelOther.replace('__COUNT__', count)
  }

  apply() {
    const url = this.buildUrl()
    // The update replaces the bar, so the new "Filters" button takes the focus afterwards
    window.ecFocusSheetButton = true
    this.trigger = null
    this.close()
    this.dispatchEvent(new CustomEvent(EVENTS.ajaxCollectionRender, { detail: { newUrl: url }, bubbles: true }))
  }

  // Swipe down on the header closes the bottom sheet (mobile only)
  initDrag(options) {
    const handle = this.querySelector('[data-ec-sheet-drag]')
    const mobile = window.matchMedia('(max-width: 768px)')
    let startY = null
    let delta = 0

    handle.addEventListener('pointerdown', (evt) => {
      if (!mobile.matches || evt.target.closest('button')) return
      startY = evt.clientY
      delta = 0
      handle.setPointerCapture(evt.pointerId)
      this.dialog.classList.add('is-dragging')
    }, options)

    handle.addEventListener('pointermove', (evt) => {
      if (startY === null) return
      delta = Math.max(0, evt.clientY - startY)
      this.dialog.style.transform = `translateY(${delta}px)`
    }, options)

    const end = () => {
      if (startY === null) return
      startY = null
      this.dialog.classList.remove('is-dragging')
      if (delta > 80) {
        this.close()
      } else {
        this.dialog.style.removeProperty('transform')
      }
    }
    handle.addEventListener('pointerup', end, options)
    handle.addEventListener('pointercancel', end, options)
  }
}

if (!customElements.get('ec-filter-sheet')) {
  customElements.define('ec-filter-sheet', EcFilterSheet)
}
